import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";

const REVIEWS = [
  { name: "Aditya R.", role: "HR Manager, IT Services", stars: 5, text: "Focused, transparent recruitment. They shortlisted candidates that were actually relevant — a rarity. Closed 6 roles inside a month." },
  { name: "Meera S.", role: "Talent Acquisition Lead", stars: 5, text: "Great communication and honest timelines. The team never over-promised and always followed through. Would engage again." },
  { name: "Karan D.", role: "Software Engineer (Placed)", stars: 5, text: "As a candidate, the experience was professional throughout — clear communication, respectful follow-ups and quality interview coordination." },
  { name: "Ritu M.", role: "Founder, EdTech Startup", stars: 5, text: "We used them for both leadership and volume hiring. Structured process, strong sourcing and a genuinely people-first approach." },
  { name: "Sanjay P.", role: "CFO, Manufacturing", stars: 5, text: "Executive search handled with complete confidentiality and speed. Delivered three senior finance hires in under 8 weeks." },
  { name: "Divya J.", role: "Data Analyst (Placed)", stars: 5, text: "They connected me with an opportunity that actually matched my skills and career direction — no spray-and-pray approach." },
];

const AVG_RATING = 4.9;
const REVIEW_COUNT = 128;

export default function GoogleReviews() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % REVIEWS.length), 4200);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="py-14 sm:py-16 md:py-24 bg-brand-soft pb-24 sm:pb-24 md:pb-24">
      <div className="container-brand">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          <div className="lg:col-span-4 min-w-0">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-brand-border text-brand-navy text-xs font-semibold">
              <GoogleIcon /> Google Reviews
            </div>
            <h2 className="mt-4 font-heading font-bold text-[24px] sm:text-3xl lg:text-4xl leading-tight tracking-tight text-brand-text break-words">
              Trusted by Employers &amp; Candidates
            </h2>
            <div className="mt-6 flex items-center gap-4">
              <div className="text-5xl font-heading font-extrabold text-brand-navy">{AVG_RATING.toFixed(1)}</div>
              <div>
                <div className="flex gap-1">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className={`w-4 h-4 ${s < Math.round(AVG_RATING) ? "text-amber-500 fill-amber-500" : "text-brand-border"}`} />
                  ))}
                </div>
                <div className="text-sm text-brand-textSoft mt-1">Based on {REVIEW_COUNT}+ verified reviews</div>
              </div>
            </div>
            <p className="mt-6 body-md">
              A consistent five-star reputation for structured recruitment,
              honest communication and quality outcomes across both employer
              and candidate experience.
            </p>
          </div>

          {/* Auto-sliding highlighted review */}
          <div className="lg:col-span-8 min-w-0">
            <div className="relative rounded-2xl border border-brand-border bg-white p-6 md:p-8 shadow-[0_20px_60px_-30px_rgba(23,32,51,0.15)]">
              <div className="flex items-center justify-between">
                <GoogleIcon size={28} />
                <div className="flex gap-1">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className="w-4 h-4 text-amber-500 fill-amber-500" />
                  ))}
                </div>
              </div>
              <p key={REVIEWS[i].name} className="mt-5 text-lg leading-relaxed text-brand-text font-medium animate-fade-in-up">
                &ldquo;{REVIEWS[i].text}&rdquo;
              </p>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-royal text-white font-heading font-bold flex items-center justify-center">
                  {REVIEWS[i].name.charAt(0)}
                </div>
                <div>
                  <div className="font-heading font-bold text-brand-navy text-sm">{REVIEWS[i].name}</div>
                  <div className="text-xs text-brand-textSoft">{REVIEWS[i].role}</div>
                </div>
              </div>
              <div className="mt-6 flex gap-1.5">
                {REVIEWS.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setI(idx)}
                    aria-label={`Show review ${idx + 1}`}
                    className={`h-1.5 rounded-full transition-all ${idx === i ? "bg-brand-royal w-6" : "bg-brand-border w-1.5"}`}
                  />
                ))}
              </div>
            </div>

            {/* Scrolling review strip */}
            <div className="mt-6 relative overflow-hidden -mx-5 sm:mx-0 px-5 sm:px-0">
              <div className="review-track flex gap-3 sm:gap-4 whitespace-nowrap">
                {[...REVIEWS, ...REVIEWS].map((r, idx) => (
                  <div key={idx} className="shrink-0 w-[240px] sm:w-[300px] bg-white rounded-xl border border-brand-border p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex gap-0.5">
                        {[...Array(r.stars)].map((_, s) => <Star key={s} className="w-3 h-3 text-amber-500 fill-amber-500" />)}
                      </div>
                      <GoogleIcon size={16} />
                    </div>
                    <p className="mt-2 text-xs text-brand-text leading-relaxed line-clamp-3 whitespace-normal">
                      &ldquo;{r.text}&rdquo;
                    </p>
                    <div className="mt-3 text-[11px] font-semibold text-brand-navy">{r.name} · <span className="text-brand-textSoft font-normal">{r.role}</span></div>
                  </div>
                ))}
              </div>
              <style>{`
                .review-track { animation: reviewscroll 42s linear infinite; }
                @keyframes reviewscroll {
                  0% { transform: translateX(0); }
                  100% { transform: translateX(-50%); }
                }
                @media (prefers-reduced-motion: reduce) { .review-track { animation: none; } }
              `}</style>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function GoogleIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.7 1.22 9.2 3.6l6.8-6.8C35.6 2.4 30.2 0 24 0 14.6 0 6.5 5.4 2.5 13.3l7.9 6.1C12.3 13.5 17.7 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.5 2.8-2.2 5.2-4.7 6.8l7.6 5.9c4.4-4.1 6.9-10.1 6.9-17.2z"/>
      <path fill="#FBBC05" d="M10.4 28.6c-.5-1.4-.8-2.9-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C.9 16.6 0 20.2 0 24s.9 7.4 2.5 10.7l7.9-6.1z"/>
      <path fill="#34A853" d="M24 48c6.2 0 11.4-2 15.2-5.5l-7.6-5.9c-2.1 1.4-4.8 2.3-7.6 2.3-6.3 0-11.7-4-13.6-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/>
    </svg>
  );
}
